import * as React from 'react';

import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Switch, Route, Redirect, useRouteMatch } from 'react-router-dom';

import AppState from '@webapp/store/state';

import TabsNav, { TabProp } from '@components/tabs-nav';
import FormContainer from '@components/layout/form-container';
import MotionOpacity from '@components/motion/motion-opacity';
import SmallNotification from '@components/small-notification';
import SidebarWrapper from '@components/layout/sidebar-wrapper';
import SidebarContainer from '@components/layout/sidebar-container';

import UserProfileSidebarCtrl from '@webapp/controllers/user-profile/user-profile-sidebar';

import VerifyPage from './profile/verify';
import WalletPage from './profile/wallet';
import ProfileGeneralPage from './profile/general';
import SecurityGeneralPage from './profile/security';
import SubscriptionPage from './profile/subscription';
import ConfirmChangesPage from './profile/confirm-changes';
import CreatePasswordPage from './create-password';
import ProfileDeactivatePage from './profile/deactivate-user';
import WalletHistoryPage from './profile/wallet/wallet-history';
import SubscriptionInvoicesPage from './profile/subscription/subscriptions-invoices/index';

import { Translate } from 'react-localize-redux';
import { push } from 'connected-react-router';
import styled from 'styled-components';

type UserProfileTabs =
  | 'general'
  | 'publishing'
  | 'label'
  | 'banking'
  | 'security';

interface ProfilePageProps {
  className?: string;
  isMobile?: boolean;
}

const mapStateToProps = (state: AppState) => {
  return {
    isMobile: state.ui.mobile,
  };
};

const initialTab = 0;

export const UserProfilePage: React.FC<ProfilePageProps> = (props) => {
  const match = useRouteMatch<{ account: string }>();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const toastNotification = useSelector(
    (state: AppState) => state.ui.toastNotification
  );

  const showSidebar =
    !location.pathname.endsWith('/account-manager') &&
    !location.pathname.endsWith('/invoices') &&
    !location.pathname.endsWith('/create-password') &&
    !location.pathname.startsWith('/user/banking/history') &&
    !location.pathname.startsWith('/user/banking/success') &&
    !location.pathname.startsWith('/user/banking/new-account');

  const isWider = location.pathname.startsWith('/user/banking/history');

  const tabsData: TabProp<UserProfileTabs>[] = React.useMemo(
    () => [
      {
        label: <Translate id="DASHBOARD.NEW_DASHBOARD.USER.MENU.SETTINGS" />,
        key: 'general' as UserProfileTabs,
      },
      {
        label: <Translate id="DASHBOARD.NEW_DASHBOARD.USER.MENU.WALLET" />,
        key: 'banking' as UserProfileTabs,
      },
      {
        label: <Translate id="DASHBOARD.NEW_DASHBOARD.USER.MENU.SECURITY" />,
        key: 'security' as UserProfileTabs,
      },
    ],
    []
  );

  const [activeTab, setActiveTab] = React.useState<UserProfileTabs>(
    tabsData[initialTab].key
  );

  const getActiveTab = (url: string) => {
    const urlArray = url.split('/');
    const lastUrl = urlArray[urlArray.length - 1];
    return lastUrl;
  };

  React.useEffect(() => {
    if (getActiveTab(location.pathname) === 'general') {
      setActiveTab(tabsData[0].key);
    } else if (getActiveTab(location.pathname) === 'label') {
      setActiveTab(tabsData[1].key);
    } else if (getActiveTab(location.pathname) === 'banking') {
      setActiveTab(tabsData[2].key);
    } else if (getActiveTab(location.pathname) === 'security') {
      setActiveTab(tabsData[3].key);
    } else {
      setActiveTab(tabsData[0].key);
    }
  }, [tabsData, location.pathname]);

  const dispatch = useDispatch();

  const handleTabClick = (value: UserProfileTabs) => {
    setActiveTab(value);
    dispatch(push(`/user/${value}`));
  };

  return (
    <MotionOpacity className={props.className}>
      <SidebarContainer className="no-margin-inline">
        {showSidebar && (
          <SidebarWrapper>
            <UserProfileSidebarCtrl />
          </SidebarWrapper>
        )}
        {showSidebar && props.isMobile && (
          <TabsNav
            className="tab-nav"
            tabs={tabsData}
            currentTab={tabsData.findIndex((tab) => tab.key === activeTab)}
            onChange={(tab) => handleTabClick(tab.key as UserProfileTabs)}
            bigFont
            activeBigFont
            overflow
          />
        )}
        <FormContainer
          hasSidebar={showSidebar}
          noMinHeight
          customWidth={isWider ? 11 : undefined}
        >
          <Switch>
            <Route
              exact
              path={`${match.path}/general`}
              render={() => <ProfileGeneralPage />}
            />
            <Route
              exact
              path={`${match.path}/banking`}
              render={() => <WalletPage />}
            />
            <Route
              exact
              path={`${match.path}/banking/history`}
              render={() => <WalletHistoryPage />}
            />
            <Route
              exact
              path={`${match.path}/subscriptions`}
              render={() => <SubscriptionPage />}
            />
            <Route
              exact
              path={`${match.path}/subscriptions/invoices/:category/:id`}
              render={() => <SubscriptionInvoicesPage />}
            />
            <Route
              exact
              path={`${match.path}/security`}
              render={() => <SecurityGeneralPage />}
            />
            <Route
              exact
              path={`${match.path}/deactivate`}
              render={() => <ProfileDeactivatePage />}
            />
            <Route
              exact
              path={`${match.path}/verify`}
              render={() => <VerifyPage account={query.get('account')} />}
            />
            <Route
              exact
              path={`${match.path}/confirm-changes`}
              render={() => <ConfirmChangesPage />}
            />
            <Route
              exact
              path={`${match.path}/create-password`}
              render={() => <CreatePasswordPage />}
            />
            <Route
              exact
              path={match.path}
              render={() => <Redirect to={`${match.path}/general`} />}
            />
          </Switch>
        </FormContainer>
        {toastNotification?.value === 'user_changes_saved' && (
          <SmallNotification
            notificationType={toastNotification.type}
            title={<Translate id={toastNotification.label} />}
          />
        )}
      </SidebarContainer>
    </MotionOpacity>
  );
};
//
export default connect(mapStateToProps)(styled(UserProfilePage)`
  .tab-nav {
    padding-inline: 24px;
  }
`);
